// 📚 API Craft Back Office - API Documentation Page
import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useModels } from '../hooks/useModels';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  parameters?: string[];
  body?: string;
  example?: string;
}

const ApiDocsPage: React.FC = () => {
  const { models } = useModels();
  const { user } = useAuth();
  const [selectedModel, setSelectedModel] = useState<string>('');

  usePageTitle('API Documentation');

  // Base endpoints
  const baseEndpoints: Endpoint[] = [
    {
      method: 'GET',
      path: '/api/v1/generic/',
      description: 'List all your models',
      example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  ${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/`
    },
    {
      method: 'POST',
      path: '/api/v1/generator/generate',
      description: 'Generate new models and tables',
      body: 'JSON with model definitions',
      example: `curl -X POST -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"models": [{"name": "Product", "fields": [...]}]}' \\
  ${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generator/generate`
    },
    {
      method: 'POST',
      path: '/api/v1/ai-assistant/query',
      description: 'Query your data using natural language',
      body: '{"message": "How many records do I have?"}',
      example: `curl -X POST -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Show me my sales data"}' \\
  ${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/ai-assistant/query`
    }
  ];

  // Generate model-specific endpoints
  const generateModelEndpoints = (modelName: string): Endpoint[] => [
    {
      method: 'GET',
      path: `/api/v1/generic/${modelName}`,
      description: `Get all ${modelName} records`,
      parameters: ['page', 'page_size', 'expand', 'related'],
      example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/${modelName}?page=1&page_size=10"`
    },
    {
      method: 'GET',
      path: `/api/v1/generic/${modelName}/{id}`,
      description: `Get specific ${modelName} record`,
      parameters: ['expand', 'related'],
      example: `curl -H "Authorization: Bearer YOUR_TOKEN" \\
  "${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/${modelName}/123"`
    },
    {
      method: 'POST',
      path: `/api/v1/generic/${modelName}`,
      description: `Create new ${modelName} record`,
      body: 'JSON with record data',
      example: `curl -X POST -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"field1": "value1", "field2": "value2"}' \\
  "${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/${modelName}"`
    },
    {
      method: 'PUT',
      path: `/api/v1/generic/${modelName}/{id}`,
      description: `Update ${modelName} record`,
      body: 'JSON with updated data',
      example: `curl -X PUT -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"field1": "new_value"}' \\
  "${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/${modelName}/123"`
    },
    {
      method: 'DELETE',
      path: `/api/v1/generic/${modelName}/{id}`,
      description: `Delete ${modelName} record`,
      example: `curl -X DELETE -H "Authorization: Bearer YOUR_TOKEN" \\
  "${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/generic/${modelName}/123"`
    }
  ];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Layout>
      <div className="space-y-6">
        
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-sage-200 p-6">
          <h1 className="text-2xl font-bold text-forest-800 mb-2">📚 API Documentation</h1>
          <p className="text-sage-600">
            Complete reference for your API Craft endpoints. All endpoints require authentication.
          </p>
          
          {user && (
            <div className="mt-4 p-4 bg-mint-50 rounded-lg border border-mint-200">
              <p className="text-sm text-forest-700">
                <strong>Your API Base URL:</strong> <code className="bg-white px-2 py-1 rounded border">{process.env.REACT_APP_API_URL || 'http://localhost:8000'}</code>
              </p>
              <p className="text-sm text-forest-700 mt-1">
                <strong>User ID:</strong> <code className="bg-white px-2 py-1 rounded border">{user.id}</code>
              </p>
            </div>
          )}
        </div>

        {/* Base Endpoints */}
        <div className="bg-white rounded-xl shadow-sm border border-sage-200 p-6">
          <h2 className="text-xl font-semibold text-forest-800 mb-4">🔧 Base Endpoints</h2>
          <div className="space-y-4">
            {baseEndpoints.map((endpoint, index) => (
              <div key={index} className="border border-sage-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getMethodColor(endpoint.method)}`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{endpoint.path}</code>
                </div>
                <p className="text-sage-600 mb-3">{endpoint.description}</p>
                
                {endpoint.parameters && (
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-forest-700 mb-1">Query Parameters:</h4>
                    <div className="flex flex-wrap gap-2">
                      {endpoint.parameters.map((param) => (
                        <code key={param} className="text-xs bg-sage-100 px-2 py-1 rounded">{param}</code>
                      ))}
                    </div>
                  </div>
                )}
                
                {endpoint.body && (
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-forest-700 mb-1">Request Body:</h4>
                    <code className="text-xs bg-sage-100 px-2 py-1 rounded">{endpoint.body}</code>
                  </div>
                )}
                
                <div className="bg-gray-50 rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-forest-700">Example:</h4>
                    <button
                      onClick={() => copyToClipboard(endpoint.example || '')}
                      className="text-xs px-2 py-1 bg-mint-100 text-mint-700 rounded hover:bg-mint-200"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">{endpoint.example}</pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Model-Specific Endpoints */}
        <div className="bg-white rounded-xl shadow-sm border border-sage-200 p-6">
          <h2 className="text-xl font-semibold text-forest-800 mb-4">📊 Your Model Endpoints</h2>
          
          {models && models.length > 0 ? (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-forest-700 mb-2">Select a model:</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="px-3 py-2 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                >
                  <option value="">Choose a model...</option>
                  {models.map((model) => (
                    <option key={model.model} value={model.model}>
                      {model.model} ({model.table_structure?.fields?.length || 0} fields)
                    </option>
                  ))}
                </select>
              </div>

              {selectedModel && (
                <div className="space-y-4">
                  {generateModelEndpoints(selectedModel).map((endpoint, index) => (
                    <div key={index} className="border border-sage-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getMethodColor(endpoint.method)}`}>
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{endpoint.path}</code>
                      </div>
                      <p className="text-sage-600 mb-3">{endpoint.description}</p>
                      
                      {endpoint.parameters && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-forest-700 mb-1">Query Parameters:</h4>
                          <div className="flex flex-wrap gap-2">
                            {endpoint.parameters.map((param) => (
                              <code key={param} className="text-xs bg-sage-100 px-2 py-1 rounded">{param}</code>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {endpoint.body && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-forest-700 mb-1">Request Body:</h4>
                          <code className="text-xs bg-sage-100 px-2 py-1 rounded">{endpoint.body}</code>
                        </div>
                      )}
                      
                      <div className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-forest-700">Example:</h4>
                          <button
                            onClick={() => copyToClipboard(endpoint.example || '')}
                            className="text-xs px-2 py-1 bg-mint-100 text-mint-700 rounded hover:bg-mint-200"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">{endpoint.example}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-lg font-medium text-forest-800 mb-2">No Models Found</h3>
              <p className="text-sage-600 mb-4">Create your first model to see available endpoints.</p>
              <a
                href="/models/create"
                className="inline-flex items-center px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors"
              >
                ✨ Create Model
              </a>
            </div>
          )}
        </div>

        {/* Authentication Info */}
        <div className="bg-white rounded-xl shadow-sm border border-sage-200 p-6">
          <h2 className="text-xl font-semibold text-forest-800 mb-4">🔐 Authentication</h2>
          <div className="space-y-3">
            <p className="text-sage-600">
              All API endpoints require authentication using a Bearer token in the Authorization header.
            </p>
            <div className="bg-gray-50 rounded p-3">
              <h4 className="text-sm font-medium text-forest-700 mb-2">Header Format:</h4>
              <code className="text-sm text-gray-700">Authorization: Bearer YOUR_ACCESS_TOKEN</code>
            </div>
            <p className="text-sm text-sage-500">
              💡 You can get your access token by inspecting the network requests in your browser's developer tools after logging in.
            </p>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default ApiDocsPage;
